import { path, Quat, Vec3 } from 'playcanvas';

import { CreateDropHandler } from './drop-handler';
import { ElementType } from './element';
import { Events } from './events';
import { loadFbxCameraAnimation } from './fbx-camera-import';
import { buildFbxExport } from './fbx-export';
import { loadFbxMesh } from './fbx-mesh-import';
import { parseFbxBuffer } from './fbx-parse-utils';
import { buildGlbExport } from './glb-export';
import { BrowserFileSystem, MappedReadFileSystem } from './io';
import { O3DV_COMPANION_EXTENSIONS, O3DV_EXTENSIONS } from './o3dv-extensions';
import { Scene } from './scene';
import { Splat } from './splat';
import { serializePly, serializePlyCompressed, SerializeSettings, serializeSog, serializeSplat, serializeViewer, SogSettings, ViewerExportSettings } from './splat-serialize';
import { localize } from './ui/localization';

// ts compiler and vscode find this type, but eslint does not
type FilePickerAcceptType = unknown;

type ExportType = 'ply' | 'splat' | 'sog' | 'viewer' | 'glb' | 'fbx';

type FileType = 'ply' | 'compressedPly' | 'splat' | 'sog' | 'htmlViewer' | 'packageViewer' | 'glb' | 'fbx';

interface SceneExportOptions {
    filename: string;
    splatIdx: 'all' | number;
    serializeSettings: SerializeSettings;

    // ply
    compressedPly?: boolean;

    // sog
    sogIterations?: number;

    // viewer
    viewerExportSettings?: ViewerExportSettings;
}

const filePickerTypes: { [key: string]: FilePickerAcceptType } = {
    'ply': {
        description: 'Gaussian Splat PLY File',
        accept: {
            'application/ply': ['.ply']
        }
    },
    'compressedPly': {
        description: 'Compressed Gaussian Splat PLY File',
        accept: {
            'application/ply': ['.ply']
        }
    },
    'sog': {
        description: 'SOG Scene',
        accept: {
            'application/x-gaussian-splat': ['.json', '.sog'],
            'image/webp': ['.webp']
        }
    },
    'lcc': {
        description: 'LCC Scene',
        accept: {
            'application/json': ['.lcc'],
            'application/octet-stream': ['.bin']
        }
    },
    'splat': {
        description: 'Splat File',
        accept: {
            'application/x-gaussian-splat': ['.splat']
        }
    },
    'ksplat': {
        description: 'KSplat File',
        accept: {
            'application/x-gaussian-splat': ['.ksplat']
        }
    },
    'spz': {
        description: 'SPZ File (Niantic)',
        accept: {
            'application/x-gaussian-splat': ['.spz']
        }
    },
    'indexTxt': {
        description: 'Colmap Poses (Images.txt)',
        accept: {
            'text/plain': ['.txt']
        }
    },
    'fbx': {
        description: 'FBX File',
        accept: {
            'application/octet-stream': ['.fbx']
        }
    },
    'glb': {
        description: 'glTF Binary File',
        accept: {
            'model/gltf-binary': ['.glb']
        }
    },
    'model': {
        description: '3D Model',
        accept: {
            'model/3d': [
                '.obj', '.stl', '.off', '.3ds', '.bim', '.3mf', '.amf', '.dae', '.wrl',
                '.3dm', '.ifc', '.step', '.stp', '.iges', '.igs', '.brep', '.fcstd'
            ]
        }
    },
    'htmlViewer': {
        description: 'Viewer HTML',
        accept: {
            'text/html': ['.html']
        }
    },
    'packageViewer': {
        description: 'Viewer ZIP',
        accept: {
            'application/zip': ['.zip']
        }
    }
};

const allImportTypes = {
    description: 'Supported Files',
    accept: {
        'application/ply': ['.ply'],
        'application/x-gaussian-splat': ['.json', '.sog', '.splat', '.ksplat', '.spz'],
        'image/webp': ['.webp'],
        'application/json': ['.lcc'],
        'application/octet-stream': ['.bin', '.fbx'],
        'text/plain': ['.txt'],
        'model/gltf-binary': ['.glb'],
        'model/gltf+json': ['.gltf'],
        'model/3d': [
            '.obj', '.mtl', '.stl', '.off', '.3ds', '.bim', '.3mf', '.amf', '.dae', '.wrl',
            '.3dm', '.ifc', '.step', '.stp', '.iges', '.igs', '.brep', '.fcstd'
        ]
    }
};

// determine if all files share a common filename prefix followed by
// a frame number, e.g. "frame0001.ply", "frame0002.ply", etc.
const isPlySequence = (filenames: string[]) => {
    if (filenames.length < 2) {
        return false;
    }

    // eslint-disable-next-line regexp/no-super-linear-backtracking
    const regex = /(.*?)(\d+)(?:\.compressed)?\.ply$/;
    const baseMatch = filenames[0].match(regex);
    if (!baseMatch) {
        return false;
    }

    for (let i = 1; i < filenames.length; i++) {
        const thisMatch = filenames[i].match(regex);
        if (!thisMatch || thisMatch[1] !== baseMatch[1]) {
            return false;
        }
    }

    return true;
};

// sog comprises a single meta.json file and zero or more .webp files
const isSog = (filenames: string[]) => {
    const count = (extension: string) => filenames.reduce((sum, f) => sum + (f.endsWith(extension) ? 1 : 0), 0);
    return count('meta.json') === 1;
};

// The LCC file contains meta.lcc, index.bin, data.bin and shcoef.bin (optional)
const isLcc = (filenames: string[]) => {
    const count = (extension: string) => filenames.reduce((sum, f) => sum + (f.endsWith(extension) ? 1 : 0), 0);
    return count('.lcc') === 1;
};

type ImportFile = {
    filename: string;
    url?: string;
    contents?: File;
    handle?: FileSystemFileHandle;
};

// The PLY header is ASCII and ends with "end_header", well within the first few kilobytes
// even for a multi-gigabyte file. Read only that much: a remote .ply is fetched with a
// Range request, and if the server ignores Range and answers 200 with the whole body the
// stream is abandoned as soon as enough bytes have arrived, so the file is never
// materialised here. importSplatModel then fetches it for real, exactly once.
const PLY_HEADER_BYTES = 8192;

const readPlyHeader = async (file: ImportFile): Promise<string> => {
    if (file.contents) {
        return file.contents.slice(0, PLY_HEADER_BYTES).text();
    }

    const controller = new AbortController();
    try {
        const response = await fetch(file.url, {
            headers: { Range: `bytes=0-${PLY_HEADER_BYTES - 1}` },
            signal: controller.signal
        });
        if (!response.ok) {
            throw new Error(`Failed to fetch file (${response.status})`);
        }
        if (!response.body) {
            // no streaming body available (very old browser): fall back to the whole response
            return (await response.text()).slice(0, PLY_HEADER_BYTES);
        }

        const reader = response.body.getReader();
        const chunks: Uint8Array[] = [];
        let received = 0;
        while (received < PLY_HEADER_BYTES) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            chunks.push(value);
            received += value.length;
        }
        // cancelling a stream that has already ended is a no-op; cancelling one that has
        // not (a server that ignored Range and is still sending) drops the rest of the body
        await reader.cancel().catch(() => {});

        const bytes = new Uint8Array(received);
        let offset = 0;
        for (const chunk of chunks) {
            bytes.set(chunk, offset);
            offset += chunk.length;
        }
        return new TextDecoder().decode(bytes.subarray(0, PLY_HEADER_BYTES));
    } catch (error) {
        controller.abort();
        throw error;
    }
};

// Decide whether a .ply is a gaussian splat or a polygon mesh.
//
// The default is SPLAT, deliberately. Every .ply that loaded as a splat before multi-format
// import must continue to, and the splat dialects in the wild are open-ended: this app alone
// writes both an uncompressed form (f_dc_0/scale_0/rot_0) and a compressed form whose header
// declares only min_x..max_b, min/max_scale_*, packed_position, packed_rotation, packed_scale
// and packed_color - none of the uncompressed markers. Other tools add further variants. So
// the mesh path is taken only on a POSITIVE mesh signal: a face element, which polygon PLYs
// essentially always declare and splat PLYs never do. Splat markers additionally veto the
// mesh path. Anything unrecognised - including an unreadable header - stays on the splat
// path, which is the pre-existing behaviour.
const SPLAT_PLY_MARKERS = [
    // uncompressed 3DGS (and the same names as written by splat-serialize.ts)
    'f_dc_0', 'scale_0', 'rot_0', 'f_rest_0',
    // this app's compressed .ply chunk and vertex properties (splat-serialize.ts)
    'packed_position', 'packed_rotation', 'packed_scale', 'packed_color',
    'min_scale_x', 'max_scale_x', 'element chunk'
];

const isSplatPly = async (file: ImportFile): Promise<boolean> => {
    const headerText = await readPlyHeader(file);
    const endIndex = headerText.indexOf('end_header');
    const header = endIndex >= 0 ? headerText.slice(0, endIndex) : headerText;

    if (SPLAT_PLY_MARKERS.some(prop => header.includes(prop))) {
        return true;
    }

    // 'element face <count>' is the mesh signal
    const hasFaceElement = /^[ \t]*element[ \t]+face[ \t]+\d+/m.test(header);
    return !hasFaceElement;
};

const vec = new Vec3();

// load inria camera poses from json file
const loadCameraPoses = async (file: ImportFile, events: Events) => {
    const response = new Response(file.contents);
    const json = await response.json();

    if (json.length > 0) {
        // sort entries by trailing number if it exists
        const sorter = (a: any, b: any) => {
            const avalue = a.id ?? a.img_name?.match(/\d*$/)?.[0];
            const bvalue = b.id ?? b.img_name?.match(/\d*$/)?.[0];
            return (avalue && bvalue) ? parseInt(avalue, 10) - parseInt(bvalue, 10) : 0;
        };

        json.sort(sorter).forEach((pose: any, i: number) => {
            if (pose.hasOwnProperty('position') && pose.hasOwnProperty('rotation')) {
                const p = new Vec3(pose.position);
                const z = new Vec3(pose.rotation[0][2], pose.rotation[1][2], pose.rotation[2][2]);

                // Use fixed offset along Z-axis direction instead of variable dot product
                vec.copy(z).mulScalar(10).add(p);

                // compute max FOV from intrinsics (vertical or horizontal, whichever is larger)
                let fov = 60;
                if (pose.fx && pose.fy && pose.width && pose.height) {
                    const fovX = 2 * Math.atan(pose.width / (2 * pose.fx)) * (180 / Math.PI);
                    const fovY = 2 * Math.atan(pose.height / (2 * pose.fy)) * (180 / Math.PI);
                    fov = Math.max(fovX, fovY);
                }

                events.fire('camera.addPose', {
                    name: pose.img_name ?? `${file.filename}_${i}`,
                    frame: i,
                    position: new Vec3(-p.x, -p.y, p.z),
                    target: new Vec3(-vec.x, -vec.y, vec.z),
                    fov
                });
            }
        });
    }
};

const removeExtension = (filename: string) => {
    return filename.substring(0, filename.length - path.getExtension(filename).length);
};

// https://colmap.github.io/format.html#images-txt
const loadImagesTxt = async (file: ImportFile, events: Events) => {
    const response = new Response(file.contents);
    const text = await response.text();

    // split into lines, remove comments and empty lines
    const poses = text.split('\n')
    .map(line => line.trim())
    .filter(line => !line.startsWith('#'))      // remove comments
    .filter((_, i) => i % 2 === 0)              // remove every second line
    .map((line, i) => {
        const parts = line.split(' ');
        if (parts.length !== 10) {
            return null;
        }
        const name = parts[9];
        const order = parseInt(removeExtension(name).match(/\d+$/)?.[0], 10);
        return {
            w: parseFloat(parts[1]),
            x: parseFloat(parts[2]),
            y: parseFloat(parts[3]),
            z: parseFloat(parts[4]),
            tx: parseFloat(parts[5]),
            ty: parseFloat(parts[6]),
            tz: parseFloat(parts[7]),
            name: name ?? `${file.filename}_${i}`,
            order: isFinite(order) ? order : i
        };
    })
    .filter(entry => !!entry)
    .sort((a, b) => (a.order < b.order ? -1 : 1));

    const q = new Quat();
    const t = new Vec3();

    poses.forEach((pose, i) => {
        const { w, x, y, z, tx, ty, tz } = pose;

        q.set(x, y, z, w).normalize().invert();
        t.set(-tx, -ty, -tz);
        q.transformVector(t, t);

        q.transformVector(Vec3.BACK, vec);
        vec.mulScalar(10).add(t);

        events.fire('camera.addPose', {
            name: pose.name,
            frame: i,
            position: new Vec3(-t.x, -t.y, t.z),
            target: new Vec3(-vec.x, -vec.y, vec.z)
        });
    });
};

// initialize file handler events
const initFileHandler = (scene: Scene, events: Events, dropTarget: HTMLElement) => {

    const showLoadError = async (message: string, filename: string) => {
        await events.invoke('showPopup', {
            type: 'error',
            header: localize('popup.error-loading'),
            message: `${message} while loading '${filename}'`
        });
    };

    // import splat model(s) - handles single files, SOG, and LCC formats
    const importSplatModel = async (files: ImportFile[], animationFrame: boolean) => {
        try {
            const filenames = files.map(f => f.filename.toLowerCase());

            // Determine the main file based on format
            let mainIndex: number;
            if (filenames.some(f => f === 'meta.json')) {
                mainIndex = filenames.findIndex(f => f === 'meta.json');
            } else if (filenames.some(f => f.endsWith('.lcc'))) {
                mainIndex = filenames.findIndex(f => f.endsWith('.lcc'));
            } else {
                mainIndex = 0;  // Single file case
            }

            const mainFile = files[mainIndex];
            const baseUrl = mainFile.url ? new URL('.', new URL(mainFile.url, window.location.href)).href : undefined;

            // Create file system with all local files, falling back to URL loading
            const fileSystem = new MappedReadFileSystem(baseUrl);
            files.forEach((f) => {
                if (f.contents) fileSystem.addFile(f.filename, f.contents);
            });

            // For URL-only single file, use full URL as filename
            const filename = (files.length === 1 && !mainFile.contents && mainFile.url) ?
                mainFile.url :
                mainFile.filename;

            const model = await scene.assetLoader.load(filename, fileSystem, animationFrame);
            await scene.add(model);
            return model;
        } catch (error) {
            const displayName = files[0]?.filename ?? 'unknown';
            await showLoadError(error.message ?? error, displayName);
        }
    };

    // import a traditional polygonal model (glB/glTF) alongside splats
    const importModel = async (file: ImportFile) => {
        try {
            const contents = file.contents ?? new File([await (await fetch(file.url)).arrayBuffer()], file.filename);
            const model = await scene.assetLoader.loadModel(contents, file.filename);
            await scene.add(model);
            return model;
        } catch (error) {
            await showLoadError(error.message ?? error, file.filename);
        }
    };

    // figure out what the set of files are (ply sequence, document, sog set, ply) and then import them
    const importFiles = async (files: ImportFile[], animationFrame = false) => {
        const filenames = files.map(f => f.filename.toLowerCase());

        const result: Splat[] = [];

        if (isPlySequence(filenames)) {
            // handle ply sequence
            events.fire('plysequence.setFrames', files.map(f => f.contents));
            events.fire('timeline.frame', 0);
        } else if (isSog(filenames) || isLcc(filenames)) {
            if (isLcc(filenames)) {
                const response = await events.invoke('showPopup', {
                    type: 'okcancel',
                    header: 'LCC',
                    message: localize('popup.lcc-upload-warning'),
                    link: `${window.location.origin}/upload`
                });
                if (response.action === 'cancel') {
                    return result;
                }
            }
            const model = await importSplatModel(files, animationFrame);
            if (model) result.push(model);
        } else {
            const hasO3dvMainFile = filenames.some(f => O3DV_EXTENSIONS.some(ext => f.endsWith(ext)));

            // check for unrecognized file types.
            //
            // The O3DV companion extensions (.mtl, .bin, .png, ...) count as known only when
            // the set also contains a main file for them to belong to. On their own they
            // match no import branch, so accepting them unconditionally would turn a dropped
            // .png from an "Unrecognized file type" error into a silent no-op.
            const knownExtensions = [
                '.ssproj', '.ply', '.splat', '.sog', '.webp', 'images.txt', '.json',
                '.ksplat', '.spz', '.glb', '.gltf', '.fbx',
                ...O3DV_EXTENSIONS,
                ...(hasO3dvMainFile ? O3DV_COMPANION_EXTENSIONS : [])
            ];
            for (let i = 0; i < filenames.length; i++) {
                const filename = filenames[i].toLowerCase();
                if (knownExtensions.every(ext => !filename.endsWith(ext))) {
                    await showLoadError('Unrecognized file type', filename);
                    return;
                }
            }

            // run one O3DV import and turn a "nothing importable here" result into a visible
            // error rather than a silent no-op
            const importO3dv = async (set: ImportFile[]) => {
                try {
                    // the import stack (Online3DViewer plus three.js) is large, so it is
                    // only fetched when a file actually needs it
                    const { loadO3dvModel } = await import('./o3dv-import');
                    if (!await loadO3dvModel(set, scene)) {
                        await showLoadError(localize('popup.import-no-model'), set[0].filename);
                    }
                } catch (error) {
                    await showLoadError(error.message ?? localize('popup.import-failed'), set[0].filename);
                }
            };

            // Online3DViewer imports a file SET, not a file: .obj resolves its .mtl and
            // separate-form glTF its .bin and textures from sibling files. But O3DV picks a
            // single main file per call, so a set with several main files would import one
            // and silently discard the rest. Group instead: one import per main file, each
            // carrying every companion in the set, so dropping three .obj files yields three
            // models. Companions are shared rather than assigned to one main file because
            // nothing here can tell which .obj a given .mtl belongs to - O3DV resolves that
            // itself from the file's own references.
            const o3dvIndices = new Set<number>();
            const o3dvMainFiles: ImportFile[] = [];
            const o3dvCompanionFiles: ImportFile[] = [];
            if (hasO3dvMainFile) {
                for (let i = 0; i < files.length; i++) {
                    const filename = filenames[i].toLowerCase();
                    if (O3DV_EXTENSIONS.some(ext => filename.endsWith(ext))) {
                        o3dvIndices.add(i);
                        o3dvMainFiles.push(files[i]);
                    } else if (O3DV_COMPANION_EXTENSIONS.some(ext => filename.endsWith(ext))) {
                        o3dvIndices.add(i);
                        o3dvCompanionFiles.push(files[i]);
                    }
                }

                for (const mainFile of o3dvMainFiles) {
                    // loadO3dvModel names the imported element after set[0], so the main file
                    // leads its companions
                    await importO3dv([mainFile, ...o3dvCompanionFiles]);
                }
            }

            // handle multiple files as independent imports
            for (let i = 0; i < files.length; i++) {
                if (o3dvIndices.has(i)) {
                    continue;
                }
                const filename = filenames[i].toLowerCase();

                if (filename.endsWith('.ssproj')) {
                    // load ssproj document. doc.load expects a File/Blob (it streams via
                    // BlobReadSource), so wrap a fetched remote scene as a named File rather
                    // than passing a raw ArrayBuffer.
                    const contents = files[i].contents ??
                        new File([await (await fetch(files[i].url)).blob()], files[i].filename);
                    await events.invoke('doc.load', contents, files[i].handle);
                } else if (['.ply', '.splat', '.sog', '.ksplat', '.spz'].some(ext => filename.endsWith(ext))) {
                    // sniffing reads the header over the network for a URL import, so a
                    // failure here must surface as the same error popup the splat path
                    // produced before sniffing existed, not as an unhandled rejection
                    let isMeshPly = false;
                    if (filename.endsWith('.ply')) {
                        try {
                            isMeshPly = !await isSplatPly(files[i]);
                        } catch (error) {
                            await showLoadError(error.message ?? error, files[i].filename);
                            continue;
                        }
                    }

                    if (isMeshPly) {
                        // a polygon-mesh .ply, not a gaussian splat
                        await importO3dv([files[i]]);
                    } else {
                        // load gaussian splat model
                        const model = await importSplatModel([files[i]], animationFrame);
                        if (model) result.push(model);
                    }
                } else if (['.glb', '.gltf'].some(ext => filename.endsWith(ext))) {
                    // load polygonal model
                    await importModel(files[i]);
                } else if (filename.endsWith('images.txt')) {
                    // load colmap frames
                    await loadImagesTxt(files[i], events);
                } else if (filename.endsWith('.json')) {
                    // load inria camera poses
                    await loadCameraPoses(files[i], events);
                } else if (filename.endsWith('.fbx')) {
                    // parse the file once and share the reader between mesh and camera
                    // import, so a malformed FBX produces a single error popup (not two)
                    // and a large binary FBX isn't fully parsed into JS arrays twice
                    let meshImported = false;
                    try {
                        const buffer = await (files[i].contents ?? await (await fetch(files[i].url)).blob()).arrayBuffer();
                        const reader = parseFbxBuffer(buffer);

                        // try mesh import first; independent of whether the file also has a
                        // camera animation, so a mesh-only FBX doesn't error on "no camera
                        // found" and a camera-only FBX (today's case) behaves exactly as before
                        try {
                            meshImported = await loadFbxMesh(reader, scene, files[i].filename);
                        } catch (error) {
                            await showLoadError(error.message ?? error, files[i].filename);
                        }

                        try {
                            await loadFbxCameraAnimation(reader, events, files[i].filename);
                        } catch (error) {
                            // don't show a second error popup for "no camera found" when we
                            // already successfully imported a mesh from this file
                            if (!meshImported || !/no camera found/i.test(error.message ?? '')) {
                                await showLoadError(error.message ?? error, files[i].filename);
                            }
                        }
                    } catch (error) {
                        // the FBX itself failed to parse - one popup for this, not two
                        await showLoadError(error.message ?? error, files[i].filename);
                    }
                }
            }
        }

        return result;
    };

    events.function('import', (files: ImportFile[], animationFrame = false) => {
        return importFiles(files, animationFrame);
    });

    // The drop and file-picker handlers cannot await importFiles, so anything that escapes
    // its internal handling would become an unhandled promise rejection - invisible to the
    // user. Funnel those entry points through here so it becomes the usual error popup.
    const importFilesDetached = (files: ImportFile[]) => {
        importFiles(files).catch(async (error) => {
            await showLoadError(error?.message ?? error, files[0]?.filename ?? 'unknown');
        });
    };

    // create a file selector element as fallback when showOpenFilePicker isn't available
    let fileSelector: HTMLInputElement;
    if (!window.showOpenFilePicker) {
        fileSelector = document.createElement('input');
        fileSelector.setAttribute('id', 'file-selector');
        fileSelector.setAttribute('type', 'file');
        fileSelector.setAttribute('accept', '.ply,.splat,meta.json,.json,.webp,.ssproj,.sog,.lcc,.bin,.txt,.ksplat,.spz,.glb,.gltf,.fbx,.obj,.mtl,.stl,.off,.3ds,.bim,.3mf,.amf,.dae,.wrl,.3dm,.ifc,.step,.stp,.iges,.igs,.brep,.fcstd');
        fileSelector.setAttribute('multiple', 'true');

        fileSelector.onchange = () => {
            const files = [];
            for (let i = 0; i < fileSelector.files.length; i++) {
                const file = fileSelector.files[i];
                files.push({
                    filename: file.name,
                    contents: file
                });
            }
            importFilesDetached(files);
            fileSelector.value = '';
        };
        document.body.append(fileSelector);
    }

    // create the file drag & drop handler
    CreateDropHandler(dropTarget, (entries, shift) => {
        importFilesDetached(entries.map((e) => {
            return {
                filename: e.filename,
                contents: e.file,
                handle: e.handle
            };
        }));
    });

    // get the list of visible splats containing gaussians
    const getSplats = () => {
        return (scene.getElementsByType(ElementType.splat) as Splat[])
        .filter(splat => splat.visible)
        .filter(splat => splat.numSplats > 0);
    };

    events.function('scene.allSplats', () => {
        return (scene.getElementsByType(ElementType.splat) as Splat[]);
    });

    events.function('scene.splats', () => {
        return getSplats();
    });

    events.function('scene.empty', () => {
        return getSplats().length === 0;
    });

    events.function('scene.hasModelContent', () => {
        return scene.getElementsByType(ElementType.model).length > 0;
    });

    events.function('scene.import', async () => {
        if (fileSelector) {
            fileSelector.click();
        } else {
            try {
                const handles = await window.showOpenFilePicker({
                    id: 'SuperSplatFileImport',
                    multiple: true,
                    excludeAcceptAllOption: false,
                    types: [
                        allImportTypes,
                        filePickerTypes.ply,
                        filePickerTypes.compressedPly,
                        filePickerTypes.splat,
                        filePickerTypes.sog,
                        filePickerTypes.lcc,
                        filePickerTypes.ksplat,
                        filePickerTypes.spz,
                        filePickerTypes.indexTxt,
                        filePickerTypes.glb,
                        filePickerTypes.fbx,
                        filePickerTypes.model
                    ]
                });

                const files = [];
                for (let i = 0; i < handles.length; i++) {
                    files.push({
                        filename: handles[i].name,
                        contents: await handles[i].getFile()
                    });
                }

                importFilesDetached(files);

            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error(error);
                }
            }
        }
    });

    // open a folder
    events.function('scene.openAnimation', async () => {
        try {
            const handle = await window.showDirectoryPicker({
                id: 'SuperSplatFileOpenAnimation',
                mode: 'readwrite'
            });

            if (handle) {
                const files = [];
                for await (const value of handle.values()) {
                    if (value.kind === 'file') {
                        const file = await value.getFile();
                        if (file.name.toLowerCase().endsWith('.ply')) {
                            files.push(file);
                        }
                    }
                }
                events.fire('plysequence.setFrames', files);
                events.fire('timeline.frame', 0);
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error(error);
            }
        }
    });

    events.function('scene.export', async (exportType: ExportType) => {
        const splats = getSplats();

        const hasFilePicker = !!window.showSaveFilePicker;

        // show viewer export options
        const options = await events.invoke('show.exportPopup', exportType, splats.map(s => s.name), !hasFilePicker) as SceneExportOptions;

        // return if user cancelled
        if (!options) {
            return;
        }

        const fileType: FileType =
            (exportType === 'viewer') ? (options.viewerExportSettings!.type === 'zip' ? 'packageViewer' : 'htmlViewer') :
                (exportType === 'ply') ? (options.compressedPly ? 'compressedPly' : 'ply') :
                    (exportType === 'sog') ? 'sog' :
                        (exportType === 'glb') ? 'glb' :
                            (exportType === 'fbx') ? 'fbx' : 'splat';

        if (hasFilePicker) {
            try {
                const fileHandle = await window.showSaveFilePicker({
                    id: 'SuperSplatFileExport',
                    types: [filePickerTypes[fileType]],
                    suggestedName: options.filename
                });
                await events.invoke('scene.write', fileType, options, await fileHandle.createWritable());
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error(error);
                }
            }
        } else {
            await events.invoke('scene.write', fileType, options);
        }
    });

    events.function('scene.write', async (fileType: FileType, options: SceneExportOptions, stream?: FileSystemWritableFileStream) => {
        // SOG and viewer exports have their own progress UI, other formats use spinner
        const useSpinner = fileType !== 'sog' && fileType !== 'htmlViewer' && fileType !== 'packageViewer';

        if (useSpinner) {
            events.fire('startSpinner');
        }

        try {
            // setTimeout so spinner/progress has a chance to activate
            await new Promise<void>((resolve) => {
                setTimeout(resolve);
            });

            const { filename, splatIdx, serializeSettings, viewerExportSettings } = options;

            // Create FileSystem for output
            const fs = new BrowserFileSystem(filename, stream);

            const splats = splatIdx === 'all' ? getSplats() : [getSplats()[splatIdx]];

            switch (fileType) {
                case 'ply':
                    await serializePly(splats, serializeSettings, fs);
                    break;
                case 'compressedPly':
                    serializeSettings.minOpacity = 1 / 255;
                    serializeSettings.removeInvalid = true;
                    await serializePlyCompressed(splats, serializeSettings, fs);
                    break;
                case 'splat':
                    await serializeSplat(splats, serializeSettings, fs);
                    break;
                case 'sog': {
                    const sogSettings: SogSettings = {
                        ...serializeSettings,
                        minOpacity: 1 / 255,
                        removeInvalid: true,
                        iterations: options.sogIterations ?? 10,
                        events
                    };
                    await serializeSog(splats, sogSettings, fs);
                    break;
                }
                case 'htmlViewer':
                case 'packageViewer':
                    await serializeViewer(splats, serializeSettings, { ...viewerExportSettings!, events }, fs);
                    break;
                case 'glb': {
                    const glbWriter = fs.createWriter('output.glb');
                    await glbWriter.write(new Uint8Array(await buildGlbExport(scene)));
                    await glbWriter.close();
                    break;
                }
                case 'fbx': {
                    const fbxWriter = fs.createWriter('output.fbx');
                    await fbxWriter.write(new TextEncoder().encode(buildFbxExport(scene)));
                    await fbxWriter.close();
                    break;
                }
                default:
                    throw new Error(`Export format '${fileType}' is not yet implemented`);
            }

        } catch (error) {
            await events.invoke('showPopup', {
                type: 'error',
                header: localize('popup.error-loading'),
                message: `${error.message ?? error} while saving file`
            });
        } finally {
            if (useSpinner) {
                events.fire('stopSpinner');
            }
        }
    });
};

export { initFileHandler, ExportType, SceneExportOptions };
